// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState } from "react";
import styles from "./DropZone.module.css";

interface BannerProps {
    onClick: any;
    onDrop: any;
    useDarkMode: boolean;
};

const Banner = ({ onClick, onDrop, useDarkMode }: BannerProps) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (ev: any) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.dataTransfer.dropEffect = "copy";
        setIsDragging(true);
    };

    const handleDragEnter = (ev: any) => {
        ev.preventDefault();
        ev.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (ev: any) => {
        ev.preventDefault();
        ev.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (ev: any) => {
        ev.preventDefault();
        ev.stopPropagation();
        setIsDragging(false);
        onDrop(ev.dataTransfer.files);
    };

    return (
        <div
            className={`${useDarkMode ? styles.bannerDark : styles.banner} ${isDragging ? useDarkMode ? styles.dragOverDark : styles.dragOver : ""}`}
            onClick={onClick}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <span className={useDarkMode ? styles.bannerTextDark : styles.bannerText}>Click to Add files</span>
            <span className={useDarkMode ? styles.bannerTextDark : styles.bannerText}>Or</span>
            <span className={useDarkMode ? styles.bannerTextDark : styles.bannerText}>Drag and Drop files here</span>
        </div>
    );
};

interface DropZoneProps {
    onChange: (files: FileList) => void;
    accept?: string[];
    useDarkMode: boolean;
}

const DropZone = ({ onChange, accept = ["*"], useDarkMode }: DropZoneProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
        inputRef.current?.click();
    };

    const handleChange = (ev: any) => {
        onChange(ev.target.files);
    };

    const handleDrop = (files: any) => {
        onChange(files);
    };

    return (
        <div className={styles.wrapper}>
            <Banner
                onClick={handleClick}
                onDrop={handleDrop}
                useDarkMode={useDarkMode}
            />
            <input
                type="file"
                aria-label="Upload Files"
                className={styles.input}
                ref={inputRef}
                multiple={true}
                onChange={handleChange}
                accept={accept.join(",")}
            />
        </div>
    );
};

export { DropZone };
